import React, { useState, useEffect, useRef } from 'react';
import Icon from '@components/core/Icon';
import useMediaQuery from '@helpers/hooks/useMediaQuery';

const styling = {
  pagination: `
    flex
    items-center
    justify-center
  `,
  pagination__button: `
    w-12
    h-12
    flex
    items-center
    justify-center
    mx-1
    rounded-full
    text-shade-900
    text-body-lg
    text-body-lg--semibold
    hover:text-primary
  `,
  'pagination__button--disabled': `
    text-shade-400
    !pointer-events-none
  `,
  'pagination__button--active': `
    !text-primary
    !pointer-events-none
  `,
  'pagination__chevron': `
    flex
    items-center
    justify-center
    mx-1
    text-shade-900
    text-body-lg
    text-body-lg--semibold
    hover:text-primary
  `,
  'pagination__chevron--disabled': `
    text-shade-400
    !pointer-events-none
  `,
  'pagination__active-mobile': `
    text-primary
  `,
};

interface IPagination  {
  dataLength: number;
  itemsPerPage: number;
  currentPage: number;
  visibleRange?: number;
  className: string;
  setCurrentPage: (currentPage: number) => void;
  baseUrl?: string; // Optional: Base URL for pagination links (falls back to NEXT_PUBLIC_BASE_URL env var or current page URL)
}

export default function Pagination(props: IPagination) {
  const paginationRef = useRef<HTMLInputElement>(null);
  const [ totalPages, setTotalPages ] = useState(1);
  const [ visibleRange, setVisibleRange ] = useState(props.visibleRange || 1);
  const isMobile = useMediaQuery(786);

  // Run once
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    const resizeHandler = () => {
      if (window.innerWidth < 500) {
        setVisibleRange(0);
      } else if (window.innerWidth < 700) {
        setVisibleRange(1);
      } else {
        setVisibleRange(1);
      }
    };

    window.addEventListener('resize', resizeHandler);
    resizeHandler();

    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  const { dataLength, itemsPerPage, currentPage, setCurrentPage, baseUrl } = props;

  // Generate pagination URL
  const getPaginationUrl = (pageNumber: number): string => {
    // During SSR, return a placeholder URL
    if (typeof window === 'undefined') {
      return baseUrl || process.env.NEXT_PUBLIC_BASE_URL || '#';
    }

    // Use provided baseUrl, env variable, or fall back to current page URL
    const currentUrl = baseUrl || process.env.NEXT_PUBLIC_BASE_URL || window.location.pathname + window.location.search;
    const url = new URL(currentUrl, window.location.origin);
    if (pageNumber > 1) {
      url.searchParams.set('page', pageNumber.toString());
    } else {
      url.searchParams.delete('page');
    }
    return url.pathname + url.search;
  };

  useEffect(() => {
    const totalPages = Math.ceil(dataLength / itemsPerPage) || 1;

    if (currentPage > totalPages) { // SHOULD NOT BE POSSIBLE
      setCurrentPage(totalPages);
    }

    setTotalPages(totalPages);
  }, [ dataLength, itemsPerPage, currentPage, setCurrentPage ]);

  const updatePage = (position: number, event?: React.MouseEvent) => {
    // If JavaScript is enabled, prevent default and use SPA navigation
    if (event) {
      event.preventDefault();
    }

    props.setCurrentPage(position);

    // Only scroll if we're in the browser
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.scrollTo({ top: (paginationRef?.current?.parentElement?.parentElement?.getBoundingClientRect().top || 0) + window.pageYOffset - 120, behavior: 'smooth' });
      }, 60);
    }
  };

  // Helper function to create pagination links
  const createPaginationLink = (
    pageNumber: number,
    children: React.ReactNode,
    className: string,
    key: string,
    isDisabled = false,
    isActive = false
  ) => {
    const href = isDisabled ? '#' : getPaginationUrl(pageNumber);
    let linkClassName = className;

    if (isDisabled) {
      linkClassName += ` ${ styling['pagination__button--disabled'] }`;
    } else if (isActive) {
      linkClassName += ` ${ styling['pagination__button--active'] }`;
    }

    return (
      <a
        href={ href }
        className={ linkClassName }
        key={ key }
        onClick={ (e) => !isDisabled && updatePage(pageNumber, e) }
      >
        { children }
      </a>
    );
  };

  const createPages = () => {
    let startPosition = props.currentPage - visibleRange;
    let endPosition = props.currentPage + visibleRange;

    if (startPosition < 3) {
      endPosition += (3 - startPosition);
    }

    if (endPosition > totalPages - 2) {
      startPosition -= (endPosition - (totalPages - 2));
    }

    startPosition = (startPosition < 2) ? 2 : startPosition;
    endPosition = (endPosition > totalPages - 1) ? totalPages - 1 : endPosition;

    const totalVisible = endPosition - startPosition;

    const pages: React.ReactElement[] = [];

    // Arrow left (previous)
    pages.push(
      createPaginationLink(
        props.currentPage - 1,
        <>
          <Icon name={ 'ChevronLeft' }  />
          <span className='ml-2'>Vorige</span>
        </>,
        styling.pagination__chevron,
        'pagination-chevron-left',
        1 === props.currentPage
      )
    );

    // Always render first and last page
    pages.push(
      createPaginationLink(
        1,
        '1',
        styling.pagination__button,
        `pagination-${ 1 }`,
        false,
        1 === props.currentPage
      )
    );

    let pageNumber = startPosition;
    let pageCount = 0;

    while (pageNumber <= endPosition) {
      const currentNumber = pageNumber;

      if (pageCount === 0) {
        if (currentNumber > 3) {
          // Render dots ... before visible range
          pages.push(
            createPaginationLink(
              Math.round((startPosition) / 2),
              '…',
              styling.pagination__button + ' pointer-events-none',
              'pagination-skip-low'
            )
          );
        } else if (currentNumber === 3) {
          // Render 2nd page
          pages.push(
            createPaginationLink(
              2,
              '2',
              styling.pagination__button,
              'pagination-2'
            )
          );
        }
      }

      // Always render the active page and its surrounding visible range
      pages.push(
        createPaginationLink(
          currentNumber,
          currentNumber.toString(),
          styling.pagination__button,
          `pagination-${ currentNumber }`,
          false,
          currentNumber === props.currentPage
        )
      );

      if (pageCount === totalVisible) {
        if (currentNumber < totalPages - 2) {
          // Render dots ... after visible range
          pages.push(
            createPaginationLink(
              Math.round((endPosition + totalPages) / 2),
              '…',
              styling.pagination__button + ' pointer-events-none',
              'pagination-skip-high'
            )
          );
        } else if (currentNumber === totalPages - 2) {
          // Render page before last
          pages.push(
            createPaginationLink(
              totalPages - 1,
              (totalPages - 1).toString(),
              styling.pagination__button,
              `pagination${ totalPages - 1 }`
            )
          );
        }
      }

      pageNumber++;
      pageCount++;
    }

    // Render last page
    if (totalPages !== 1) {
      pages.push(
        createPaginationLink(
          totalPages,
          totalPages.toString(),
          styling.pagination__button,
          `pagination-${ totalPages }`,
          false,
          totalPages === props.currentPage
        )
      );
    }

    // Arrow right (next)
    pages.push(
      createPaginationLink(
        props.currentPage + 1,
        <>
          <span className='mr-2'>Volgende</span>
          <Icon name={ 'ChevronRight' }  />
        </>,
        styling.pagination__chevron,
        'pagination-chevron-right',
        totalPages === props.currentPage
      )
    );

    return pages;
  };

  return (
    <>
      { totalPages > 1 && !isMobile &&
        <div
          className={ styling.pagination + ` ${ props.className ?? '' }` }
          ref={ paginationRef }
        >
          { createPages() }
        </div>
      }
      {
        totalPages > 1 && isMobile &&
        <div
          className={ styling.pagination + ` ${ props.className ?? '' }` }
          ref={ paginationRef }
        >
          {createPaginationLink(
            props.currentPage - 1,
            <>
              <Icon name={ 'ChevronLeft' }  />
              <span className='ml-2'>Vorige</span>
            </>,
            styling.pagination__chevron,
            'pagination-chevron-left',
            1 === props.currentPage
          )}
          <div
            className={ 'text-body-md whitespace-nowrap' }
          >
            <span className={ 'text-body-md text-body-md--semibold text-primary ml-6' }>
              { props.currentPage }
            </span>
            <span className='mr-6'> / { totalPages }</span>
          </div>
          {createPaginationLink(
            props.currentPage + 1,
            <>
              <span className='mr-2'>Volgende</span>
              <Icon name={ 'ChevronRight' }  />
            </>,
            styling.pagination__chevron,
            'pagination-chevron-right',
            totalPages === props.currentPage
          )}
        </div>
      }
    </>
  );
}
